import { get, post, patch, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';

export interface EmployeeAllowanceItem {
  label: string;
  amount: number;
}

export interface Employee {
  id: string;
  employeeCode: string;
  type: string;
  firstName?: string;
  lastName?: string;
  name: string;
  dateOfBirth?: string;
  gender?: string;
  mobileNumber?: string;
  email?: string;
  address?: string;
  departmentId?: string;
  designationId?: string;
  specializationIds?: string[];
  joiningDate?: string;
  employmentType?: string;
  compensationType?: string;
  basicSalary?: number;
  allowanceItems?: EmployeeAllowanceItem[];
  taxDeduction?: number;
  revenueShareEligible?: boolean;
  bankAccountNumber?: string;
  bankName?: string;
  biometricDeviceId?: string;
  nationalIdFrontUrl?: string;
  nationalIdBackUrl?: string;
  qualifications?: string[];
  registrationNumber?: string;
  photoUrl?: string;
  signatureUrl?: string;
  revenueSharePercent?: number;
  payrollPolicyId?: string;
  payrollPolicy?: { id: string; name: string } | null;
  weeklyOffDays?: number[] | null;
  workingDaysPerMonth?: number | null;
  payrollEnabled?: boolean;
  userId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  department?: {
    name: string;
  };
  designation?: {
    name: string;
  };
  specializations?: Array<{
    id: string;
    label: string;
  }>;
  user?: {
    id: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    mobile: string;
    roles: string[];
    customRoles?: { id: string; name: string; permissions: { id: string; code: string }[] }[];
    isActive: boolean;
    dashboardAccess?: boolean;
    lastLoginAt?: string;
  };
}

export interface CreateEmployeeDto {
  employeeCode?: string;
  type: string;
  firstName?: string;
  lastName?: string;
  name: string;
  dateOfBirth?: string;
  gender?: string;
  mobileNumber?: string;
  email?: string;
  address?: string;
  departmentId: string;
  designationId?: string;
  specializationIds?: string[];
  joiningDate?: string;
  employmentType?: string;
  compensationType?: string;
  basicSalary?: number;
  allowanceItems?: EmployeeAllowanceItem[];
  taxDeduction?: number;
  revenueShareEligible?: boolean;
  bankAccountNumber?: string;
  bankName?: string;
  biometricDeviceId?: string;
  nationalIdFrontUrl?: string;
  nationalIdBackUrl?: string;
  qualifications?: string[];
  registrationNumber?: string;
  photoUrl?: string;
  signatureUrl?: string;
  revenueSharePercent?: number;
  payrollPolicyId?: string | null;
  weeklyOffDays?: number[] | null;
  workingDaysPerMonth?: number | null;
  payrollEnabled?: boolean;
  userId?: string;
  isActive?: boolean;
  dashboardAccess?: boolean;
  // User account fields
  username?: string;
  password?: string;
  roles?: string[];
  customRoleIds?: string[];
}

export const employeeService = {
  getEmployees: (
    page = 1,
    limit = 20,
    filters?: { search?: string; departmentId?: string; designationId?: string; specializationId?: string; status?: string; payrollEnabled?: string; websiteRole?: string }
  ): Promise<ApiResponse<Employee[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.search) params.set('search', filters.search);
    if (filters?.departmentId) params.set('departmentId', filters.departmentId);
    if (filters?.designationId) params.set('designationId', filters.designationId);
    if (filters?.specializationId) params.set('specializationId', filters.specializationId);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.payrollEnabled) params.set('payrollEnabled', filters.payrollEnabled);
    if (filters?.websiteRole) params.set('websiteRole', filters.websiteRole);
    return get<Employee[]>(`/employees?${params.toString()}`) as Promise<ApiResponse<Employee[]> & { meta: PaginationMeta }>;
  },

  getEmployee: (id: string): Promise<ApiResponse<Employee>> => {
    return get<Employee>(`/employees/${id}`);
  },

  getNextCode: (): Promise<ApiResponse<{ code: string }>> => {
    return get<{ code: string }>('/employees/next-code');
  },

  createEmployee: (data: CreateEmployeeDto): Promise<ApiResponse<Employee>> => {
    return post<Employee>('/employees', data);
  },

  updateEmployee: (id: string, data: Partial<CreateEmployeeDto>): Promise<ApiResponse<Employee>> => {
    return patch<Employee>(`/employees/${id}`, data);
  },

  deleteEmployee: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/employees/${id}`);
  },

  /** Enable or disable payroll for many employees in one request. */
  setBulkPayrollEnabled: (
    ids: string[],
    payrollEnabled: boolean,
  ): Promise<ApiResponse<{ updated: number }>> => {
    return patch<{ updated: number }>('/employees/bulk-payroll', { ids, payrollEnabled });
  },

  /** List soft-deleted employees (the Trash page). */
  getTrashedEmployees: (
    page = 1,
    limit = 20,
    filters?: { search?: string; departmentId?: string; designationId?: string; specializationId?: string; status?: string }
  ): Promise<ApiResponse<Employee[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.search) params.set('search', filters.search);
    if (filters?.departmentId) params.set('departmentId', filters.departmentId);
    if (filters?.designationId) params.set('designationId', filters.designationId);
    if (filters?.specializationId) params.set('specializationId', filters.specializationId);
    if (filters?.status) params.set('status', filters.status);
    return get<Employee[]>(`/employees/trash?${params.toString()}`) as Promise<ApiResponse<Employee[]> & { meta: PaginationMeta }>;
  },

  /** Restore trashed employees back to the active list. */
  restoreEmployees: (ids: string[]): Promise<ApiResponse<{ restored: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>> => {
    return post<{ restored: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>('/employees/trash/bulk-restore', { ids });
  },

  /** Permanently delete trashed employees. */
  permanentlyDeleteEmployees: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>> => {
    return post<{ deleted: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>('/employees/trash/bulk-delete', { ids });
  },
};
